import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { useKeyboardOpen } from "./useKeyboardOpen";

// Some steps (the observation with its two cards, starters and assistant; the keepsake report)
// run past one phone screen, and on a dark full-bleed frame nothing says so - the fold looks
// like the end. This is a small fixed chevron + label at the bottom edge, shown only while
// there is more page below the viewport and gone once the player reaches the end (or when the
// content fits). Purely an affordance: aria-hidden, no interaction, scrolling is unchanged.
// Hidden while the on-screen keyboard is up so it never sits over the text being typed.
const THRESHOLD_PX = 16;

export const hasMoreBelow = (doc = document, win = window) => {
  const root = doc.documentElement;
  const scrollHeight = Math.max(root.scrollHeight, doc.body ? doc.body.scrollHeight : 0);
  return scrollHeight - (win.innerHeight + win.scrollY) > THRESHOLD_PX;
};

const ScrollCue = () => {
  const { t } = useTranslation("views");
  const keyboardOpen = useKeyboardOpen();
  const [moreBelow, setMoreBelow] = useState(false);

  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      setMoreBelow(hasMoreBelow());
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    // Content grows without a scroll or resize (a panel expands, the step animates in), so
    // watch the document body too where the browser supports it.
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    if (observer) observer.observe(document.body);
    // The step enters over a few hundred ms; re-measure once it has settled.
    const settled = window.setTimeout(schedule, 600);

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (observer) observer.disconnect();
      window.clearTimeout(settled);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  const visible = moreBelow && !keyboardOpen;

  return (
    <div
      className={`topik-scroll-cue topik-no-print${visible ? " topik-scroll-cue--visible" : ""}`}
      aria-hidden="true"
    >
      <span className="topik-scroll-cue__label">{t("topik-view.scroll-cue")}</span>
      <KeyboardArrowDownIcon className="topik-scroll-cue__icon" />
    </div>
  );
};

export default ScrollCue;
